import type { ProjectLoadReport } from '@/utils/projectLoadProgress';

// An optional transport for the current logical container. A fixed header
// declares its exact expanded length before the native inflater sees any data.
export const COMPRESSED_PROJECT_MAGIC = 'DGTLZLB6';
export const COMPRESSED_PROJECT_MAGICS = [COMPRESSED_PROJECT_MAGIC, 'DGTLZLB5'] as const;
const RAW_MAGIC = 'DGTLCMP6';
const HEADER_BYTES = 16;
const MIN_COMPRESS_BYTES = 4096;
const INFLATE_INPUT_BYTES = 4096;

/** Compress in the save worker; keep small/incompressible files as raw current containers.
 * Hosts lacking native compression write ordinary, fully readable raw files. */
export async function compressProjectContainer(raw: Blob): Promise<Blob> {
  if (
    raw.size < MIN_COMPRESS_BYTES ||
    typeof CompressionStream !== 'function' ||
    typeof DecompressionStream !== 'function' ||
    typeof raw.arrayBuffer !== 'function'
  )
    return raw;
  if (raw.size > 0xffffffff)
    throw new RangeError('Compressed project length exceeds limit');
  if ((await raw.slice(0, 8).text()) !== RAW_MAGIC)
    throw new TypeError('Only raw current projects can be compressed');
  const limit = raw.size - HEADER_BYTES - Math.max(128, Math.ceil(raw.size / 100));
  let compressor: CompressionStream;
  try {
    compressor = new CompressionStream('deflate');
    new DecompressionStream('deflate');
  } catch {
    return raw;
  }
  const zlib = await transformBlob(raw, compressor, 0, 64 * 1024, limit);
  if (!zlib) return raw;
  if (!(await validZlibFraming(zlib))) return raw;
  const header = new ArrayBuffer(HEADER_BYTES);
  new Uint8Array(header).set(new TextEncoder().encode(COMPRESSED_PROJECT_MAGIC));
  new DataView(header).setUint32(8, raw.size, true);
  return new Blob([header, zlib], { type: raw.type });
}

/** Unwrap one bounded, checksum-checked native zlib transport. Raw and released
 * containers pass through unchanged; nested transports are never accepted. */
export async function decompressProjectContainer(
  file: Blob,
  maximumBytes: number,
  report?: ProjectLoadReport,
  signal?: AbortSignal,
): Promise<Blob> {
  signal?.throwIfAborted();
  if (!Number.isSafeInteger(maximumBytes) || maximumBytes < HEADER_BYTES)
    throw new RangeError('Invalid project expansion budget');
  const magic = await file.slice(0, 8).text();
  signal?.throwIfAborted();
  if (!(COMPRESSED_PROJECT_MAGICS as readonly string[]).includes(magic)) return file;
  if (file.size < HEADER_BYTES + 6 || file.size > maximumBytes)
    throw new TypeError('Invalid compressed project size');
  const header = new DataView(await file.slice(0, HEADER_BYTES).arrayBuffer());
  const expected = header.getUint32(8, true);
  if (header.getUint32(12, true) || expected < HEADER_BYTES || expected > maximumBytes)
    throw new RangeError('Invalid compressed project expansion length');
  if (!(await validZlibFraming(file.slice(HEADER_BYTES))))
    throw new TypeError('Invalid compressed project zlib framing');
  signal?.throwIfAborted();
  if (typeof DecompressionStream !== 'function')
    throw new Error(
      'This browser cannot open compressed DigiTiles projects. Use a current browser.',
    );
  let inflater: DecompressionStream;
  try {
    inflater = new DecompressionStream('deflate');
  } catch (cause) {
    throw new Error(
      'This browser cannot open compressed DigiTiles projects. Use a current browser.',
      { cause },
    );
  }
  const raw = await transformBlob(
    file,
    inflater,
    HEADER_BYTES,
    INFLATE_INPUT_BYTES,
    expected,
    report,
    signal,
  );
  if (!raw) throw new RangeError('Compressed project exceeds declared expansion');
  if (raw.size !== expected) throw new TypeError('Truncated compressed project');
  if (
    (await raw.slice(0, 8).text()) !==
    (magic === COMPRESSED_PROJECT_MAGIC ? RAW_MAGIC : 'DGTLCMP5')
  )
    throw new TypeError('Unexpected compressed project payload');
  signal?.throwIfAborted();
  return raw;
}

/** Controlled input flow on both native transforms. pipeThrough can queue an
 * entire input on hosts whose writable strategy counts chunks rather than bytes.
 * Output is snapshotted as it arrives; codec workspace remains host-dependent. */
async function transformBlob(
  file: Blob,
  transform: CompressionStream | DecompressionStream,
  start: number,
  inputBytes: number,
  outputLimit: number,
  report?: ProjectLoadReport,
  signal?: AbortSignal,
): Promise<Blob | undefined> {
  const reader = transform.readable.getReader();
  const writer = transform.writable.getWriter();
  const abort = () => {
    void Promise.allSettled([
      writer.abort(signal?.reason),
      reader.cancel(signal?.reason),
    ]);
  };
  signal?.addEventListener('abort', abort, { once: true });
  let failed = false;
  let pumpError: unknown;
  const pump = (async () => {
    for (let offset = start; offset < file.size; ) {
      signal?.throwIfAborted();
      const end = Math.min(file.size, offset + inputBytes);
      const chunk = new Uint8Array(await file.slice(offset, end).arrayBuffer());
      signal?.throwIfAborted();
      offset = end;
      report?.('reading', offset, file.size);
      signal?.throwIfAborted();
      await writer.write(chunk);
    }
    await writer.close();
  })().catch(async (error) => {
    failed = true;
    pumpError = error;
    await reader.cancel(error).catch(() => {});
  });
  const chunks: Blob[] = [];
  let size = 0,
    finished = false;
  try {
    while (true) {
      signal?.throwIfAborted();
      const { done, value } = await reader.read();
      signal?.throwIfAborted();
      if (done) {
        finished = true;
        break;
      }
      size += value.byteLength;
      if (size > outputLimit) return undefined;
      chunks.push(new Blob([value]));
    }
  } finally {
    try {
      if (!finished || failed)
        await Promise.allSettled([writer.abort(), reader.cancel()]);
      await pump;
    } finally {
      signal?.removeEventListener('abort', abort);
      writer.releaseLock();
      reader.releaseLock();
    }
  }
  if (failed) throw pumpError;
  return new Blob(chunks, { type: file.type });
}

/** RFC1950 header: DEFLATE, supported window, no preset dictionary, FCHECK.
 * Native deflate decompression also verifies Adler32 and rejects trailing data. */
async function validZlibFraming(zlib: Blob): Promise<boolean> {
  if (zlib.size < 6) return false;
  const header = new Uint8Array(await zlib.slice(0, 2).arrayBuffer());
  return (
    (header[0] & 15) === 8 &&
    header[0] >>> 4 <= 7 &&
    !(header[1] & 32) &&
    ((header[0] << 8) + header[1]) % 31 === 0
  );
}
